import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class GeneralUtilsService {

  /**
   * Return true if str is UUID format
   * @param str
   * @param version
   */
  public isUUID(str: string, version: '3' | '4' | '5' | 'all' = 'all'): boolean {
    const uuid: Record<typeof version, RegExp> = {
      3: /^[0-9A-F]{8}-[0-9A-F]{4}-3[0-9A-F]{3}-[0-9A-F]{4}-[0-9A-F]{12}$/i,
      4: /^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i,
      5: /^[0-9A-F]{8}-[0-9A-F]{4}-5[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i,
      all: /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i,
    };
    const pattern: RegExp = uuid[version];
    return pattern?.test(str) ?? false;
  }

  public isObjectEmpty(value: any): boolean {
    if (value == null) return true;
    if (typeof value === 'number') return false;
    if (typeof value === 'string' || Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  }

  public hexToRgbA(hex: string, o: number): string {
    let c: any;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      c = hex.substring(1).split('');
      if (c.length == 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]];
      }
      c = '0x' + c.join('');
      return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',' + o + ')';
    }
    throw new Error('Bad Hex');
  }

  public getCacheBusterUrl(value: string): string {
    try {
      const url: URL = new URL(value);
      url.searchParams.set('v', (+new Date().getTime()).toString());
      return url.toString();
    } catch {
      return value;
    }
  }

  //this is just quick check if the image is base64 encoded
  public isBase64Image(value: string): boolean {
    if (value && value.length > 0) {
      return value.indexOf('data:image') > -1;
    }
    return false;
  }

  public copyToClipboard(text: string): void {
    const selBox: HTMLTextAreaElement = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = text;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
  }

  public isPlainObject(obj: any): boolean {
    if (obj === null || typeof obj !== 'object') return false;
    const proto: any = Object.getPrototypeOf(obj);
    return proto === Object.prototype || proto === null;
  }

  public debounce<T extends (...args: any[]) => void>(fn: T, delay = 300): T & { cancel: () => void } {
    let timer: ReturnType<typeof setTimeout> | null = null;
    function debounced(this: unknown, ...args: Parameters<T>) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    }
    debounced.cancel = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
    return debounced as T & { cancel: () => void };
  }

  public uniqBy<T>(array: T[], key: keyof T): T[] {
    const seen: Set<T[keyof T]> = new Set();
    return array.filter((item: T) => {
      const val: T[keyof T] = item[key];
      if (seen.has(val)) return false;
      seen.add(val);
      return true;
    });
  }

  public setByPath(obj: any, path: string, value: any): void {
    if (!obj || typeof path !== 'string') return;
    const keys: string[] = path.replace(/\[(\w+)\]/g, '.$1').split('.');
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (current[key] == null || typeof current[key] !== 'object') {
        current[key] = /^\d+$/.test(keys[i + 1]) ? [] : {};
      }
      current = current[key];
    }
    current[keys[keys.length - 1]] = value;
  }

  public compact<T>(array: T[]): T[] {
    return array.filter(Boolean);
  }

  public groupBy<T extends Record<string, any>>(
    array: T[],
    key: keyof T | ((item: T) => string | number)
  ): Record<string, T[]> {
    return array.reduce(
      (acc: Record<string, T[]>, item: T) => {
        // eslint-disable-next-line @typescript-eslint/typedef
        const groupKey = typeof key === 'function' ? key(item) : String(item[key]);
        (acc[groupKey] ||= []).push(item);
        return acc;
      },
      {} as Record<string, T[]>
    );
  }

  public getByPath(obj: any, path: string, defaultValue?: any): any {
    if (!path || typeof path !== 'string') return defaultValue;
    const normalizedPath: string = path.replace(/\[(\w+)\]/g, '.$1').replace(/^\./, '');
    const keys: string[] = normalizedPath.split('.');
    return keys.reduce((acc, key) => (acc && key in acc ? acc[key] : undefined), obj) ?? defaultValue;
  }

  public customCloneDeep<T>(value: T, cache = new WeakMap()): T {
    if (value === null || typeof value !== 'object') {
      return value;
    }
    if (cache.has(value)) {
      return cache.get(value);
    }
    if (typeof value === 'function') {
      return value;
    }
    if (value instanceof Date) {
      return new Date(value.getTime()) as any;
    }
    if (value instanceof RegExp) {
      return new RegExp(value.source, value.flags) as any;
    }
    if (Array.isArray(value)) {
      const arr: any[] = [];
      cache.set(value, arr);
      for (const item of value) {
        arr.push(this.customCloneDeep(item, cache));
      }
      return arr as any;
    }
    if (value instanceof Map) {
      const m = new Map();
      cache.set(value, m);
      value.forEach((v, k) => m.set(k, this.customCloneDeep(v, cache)));
      return m as any;
    }
    if (value instanceof Set) {
      const s = new Set();
      cache.set(value, s);
      value.forEach((v) => s.add(this.customCloneDeep(v, cache)));
      return s as any;
    }
    if (ArrayBuffer.isView(value)) {
      return new (value.constructor as any)(value) as any;
    }
    if (value instanceof ArrayBuffer) {
      return value.slice(0) as any;
    }
    const result = Object.create(Object.getPrototypeOf(value));
    cache.set(value, result);
    for (const key of Reflect.ownKeys(value)) {
      (result as any)[key] = this.customCloneDeep((value as any)[key], cache);
    }
    return result;
  }
}
